import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { styled } from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Collapse } from '@sorare/core/src/atoms/layout/Collapse';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelL } from '@sorare/core/src/atoms/typography';
import { glossary } from '@sorare/core/src/lib/glossary';

import { calculateScoreWithBonus } from 'lib/sports';

import { YourCard } from './YourCard';
import type { YourCards_AnyCardInterface } from './__generated__/index.graphql';

const StyledPlayerRow = styled(ButtonBase)`
  transition: opacity 0.2s;
`;

const PlayerList = styled(Vertical)`
  &:hover ${StyledPlayerRow} {
    opacity: 0.7;
  }
  &:hover ${StyledPlayerRow}:hover {
    opacity: 1;
  }
`;
type Props = {
  cards: YourCards_AnyCardInterface[];
  onPlayerDetailsClick?: (playerSlug: string) => void;
};

export const YourCards = ({ cards, onPlayerDetailsClick }: Props) => {
  const [showAllCards, setShowAllCards] = useState(false);
  if (cards.length === 0) {
    return null;
  }
  const itemsToDisplay = 3;
  const sortedCards = [...cards].sort((a, b) => {
    return (
      calculateScoreWithBonus(
        parseFloat(b.power),
        b.anyPlayer.playerGameScore?.score ?? 0
      ).score -
      calculateScoreWithBonus(
        parseFloat(a.power),
        a.anyPlayer.playerGameScore?.score ?? 0
      ).score
    );
  });
  const visibleCards = sortedCards.slice(0, itemsToDisplay);
  const remainingCards = sortedCards.slice(itemsToDisplay);
  return (
    <Vertical gap={1.5}>
      <LabelL bold>
        <FormattedMessage {...glossary.yourCards} />
      </LabelL>
      <PlayerList gap={1.5}>
        {visibleCards.map(card => {
          return (
            <StyledPlayerRow
              key={card.slug}
              onClick={() => onPlayerDetailsClick?.(card.anyPlayer.slug)}
            >
              <YourCard card={card} />
            </StyledPlayerRow>
          );
        })}
      </PlayerList>
      {remainingCards.length > 0 && (
        <Vertical gap={0}>
          <Collapse open={showAllCards} gap={0}>
            <PlayerList gap={1.5}>
              {remainingCards.map(card => {
                return (
                  <StyledPlayerRow
                    key={card.slug}
                    onClick={() => onPlayerDetailsClick?.(card.anyPlayer.slug)}
                  >
                    <YourCard card={card} />
                  </StyledPlayerRow>
                );
              })}
            </PlayerList>
          </Collapse>
          <Button
            color="quaternary"
            size="medium"
            onClick={() => setShowAllCards(v => !v)}
          >
            <FormattedMessage
              {...(showAllCards ? glossary.showLess : glossary.showMore)}
            />
          </Button>
        </Vertical>
      )}
    </Vertical>
  );
};

YourCards.fragments = {
  AnyCardInterface: gql`
    fragment YourCards_AnyCardInterface on AnyCardInterface {
      slug
      anyPlayer {
        slug
        playerGameScore(gameId: $gameIdWithoutPrefix) {
          id
          score
          scoreStatus
        }
      }
      ...YourCard_anyCardInterface
    }
    ${YourCard.fragments.anyCardInterface}
  ` as TypedDocumentNode<YourCards_AnyCardInterface>,
};
